// Inline rest-between-sets timer. Mounts beneath a strength MovementCard once
// the athlete has logged at least one set. Counts UP from 0:00 so the user
// knows how long they've been resting; auto-resets to zero (and restarts)
// every time `runId` changes — i.e. every new logged set. A small × hides it
// for that movement until the next set is logged.

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Timer as TimerIcon, StopCircle, ArrowCounterClockwise } from "@phosphor-icons/react";

const fmt = (s) => {
  const t = Math.max(0, Math.floor(s));
  const m = Math.floor(t / 60);
  const r = t % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

export default function RestTimer({ runId, onHide, testIdSuffix }) {
  const [seconds, setSeconds] = useState(0);
  const startedAtRef = useRef(Date.now());

  // Reset the moment a new set is logged.
  useEffect(() => {
    startedAtRef.current = Date.now();
    setSeconds(0);
  }, [runId]);

  // 1 Hz tick — cheap and accurate enough for a rest clock.
  useEffect(() => {
    const id = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Color crosses from neutral → moss (good rest) at 60s, terracotta past 3 min
  // to gently nudge the athlete back on the bar.
  const accent =
    seconds >= 180 ? "text-terracotta"
    : seconds >= 60 ? "text-moss"
    : "text-ink-2";

  return (
    <div
      className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-muted2/70 border border-line px-3 py-2.5 animate-fade-up flex-wrap"
      data-testid={`rest-timer-${testIdSuffix}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 min-w-0">
        <TimerIcon size={14} className={accent} weight="bold" />
        <span className="hs-label text-[10px] text-ink-3">Rest</span>
        <span className={`font-data font-semibold text-base tabular-nums ${accent} pr-1`} data-testid={`rest-timer-value-${testIdSuffix}`}>
          {fmt(seconds)}
        </span>
      </div>
      <div className="flex items-center gap-2 ml-auto shrink-0">
        <Button
          size="sm" variant="ghost"
          onClick={() => { startedAtRef.current = Date.now(); setSeconds(0); }}
          className="rounded-lg h-8 px-3 text-xs text-ink-2 hover:bg-line/60"
          aria-label="Reset rest timer"
          title="Restart from 0:00"
          data-testid={`rest-timer-reset-${testIdSuffix}`}
        >
          <ArrowCounterClockwise size={13} weight="bold" className="mr-1.5" /> Reset
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={onHide}
          className="rounded-lg h-8 px-3 text-xs text-terracotta hover:bg-terracotta/10 hover:text-terracotta"
          aria-label="Stop rest timer"
          title="Stop and hide the rest timer"
          data-testid={`rest-timer-stop-${testIdSuffix}`}
        >
          <StopCircle size={13} weight="fill" className="mr-1.5" /> Stop
        </Button>
      </div>
    </div>
  );
}
